import { useEffect, useRef, useState } from 'react'
import { MOTION } from '../lib/motion'

const TYPE_MS = 10
const PLATE_CLASS = 'hub-flat-backplate site-frame p-4 hub-heading-plate'

const readPrefersReducedMotion = () =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const HubSlideHeading = ({
  headingEpoch = -1,
  title,
  copy,
  landed,
  titleClassName = '',
  compact = false,
  beforeCopy = null,
  children = null,
  isActive = false,
}) => {
  const plateRef = useRef(null)
  const timersRef = useRef(null)
  const [titleIn, setTitleIn] = useState(false)
  const [typed, setTyped] = useState('')
  const [typing, setTyping] = useState(false)

  const clearTimers = () => {
    const timers = timersRef.current
    if (!timers) return
    if (timers.slide && timers.onSlideLanded) {
      timers.slide.removeEventListener('transitionend', timers.onSlideLanded)
    }
    window.clearTimeout(timers.fallback)
    window.clearInterval(timers.typeTick)
    timersRef.current = null
  }

  useEffect(() => {
    if (!landed || headingEpoch < 0) {
      if (!landed) {
        clearTimers()
        setTitleIn(false)
        setTyped('')
        setTyping(false)
      }
      return undefined
    }

    clearTimers()

    if (readPrefersReducedMotion()) {
      setTitleIn(true)
      setTyped(copy || '')
      setTyping(false)
      return undefined
    }

    setTitleIn(false)
    setTyped('')
    setTyping(false)

    const slide = plateRef.current?.closest('[data-hub-slide]')
    const waitMs = MOTION.ms.nav + 100

    let started = false

    const beginTyping = () => {
      if (!copy) return
      setTyping(true)
      if (!timersRef.current) return
      const startedAt = performance.now()
      let lastIndex = 0
      timersRef.current.typeTick = window.setInterval(() => {
        const index = Math.min(
          copy.length,
          Math.floor((performance.now() - startedAt) / TYPE_MS)
        )
        if (index === lastIndex) return
        lastIndex = index
        setTyped(copy.slice(0, index))
        if (index >= copy.length) {
          window.clearInterval(timersRef.current?.typeTick)
          if (timersRef.current) timersRef.current.typeTick = 0
          setTyping(false)
        }
      }, 16)
    }

    const handleStartHeading = () => {
      if (started) return
      started = true
      if (timersRef.current?.slide && timersRef.current?.onSlideLanded) {
        timersRef.current.slide.removeEventListener(
          'transitionend',
          timersRef.current.onSlideLanded
        )
      }
      window.clearTimeout(timersRef.current?.fallback)
      setTitleIn(true)
      beginTyping()
    }

    const handleSlideLanded = (event) => {
      if (event.target !== slide) return
      if (event.propertyName && event.propertyName !== 'transform') return
      handleStartHeading()
    }

    timersRef.current = {
      slide,
      onSlideLanded: handleSlideLanded,
      fallback: 0,
      typeTick: 0,
    }

    slide?.addEventListener('transitionend', handleSlideLanded)
    timersRef.current.fallback = window.setTimeout(handleStartHeading, waitMs)

    return clearTimers
  }, [landed, copy, headingEpoch])

  return (
    <div ref={plateRef} className={PLATE_CLASS}>
      <div className={`hub-flat-heading${compact ? ' hub-flat-heading--compact' : ''}`}>
        <div className="hub-heading-title-slot">
          {isActive ? (
            <h1
              className={[
                'site-label hub-flat-title hub-heading-title',
                titleClassName,
                titleIn ? 'is-in' : '',
              ].filter(Boolean).join(' ')}
            >
              {title}
            </h1>
          ) : (
            <h2
              className={[
                'site-label hub-flat-title hub-heading-title',
                titleClassName,
                titleIn ? 'is-in' : '',
              ].filter(Boolean).join(' ')}
            >
              {title}
            </h2>
          )}
        </div>
        {beforeCopy ? (
          <div className="hub-heading-title-slot">
            <div className={`hub-heading-title${titleIn ? ' is-in' : ''}`}>
              {beforeCopy}
            </div>
          </div>
        ) : null}
        {copy ? (
          <p className="site-muted hub-flat-copy normal-case">
            <span className="hub-flat-copy-ghost" aria-hidden="true">
              {copy}
            </span>
            <span className="hub-flat-copy-typed">
              {typed}
              {typing ? <span className="hub-type-caret" /> : null}
            </span>
          </p>
        ) : null}
        {children ? (
          <div className={titleIn ? 'opacity-100' : 'opacity-0'}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default HubSlideHeading
